import { MAX_PROP_DIVISIONS, type PropBounds } from './propDrawingPrimitives';
import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

export interface FramedPaneOptions { readonly pattern?: 'cross' | 'diamond'; readonly border?: number }
export interface StripedFabricOptions { readonly stripes?: number; readonly valance?: number }

function usableBounds(b: PropBounds): boolean {
  return [b.x, b.y, b.width, b.height, b.x + b.width, b.y + b.height].every(Number.isFinite)
    && b.width > 0 && b.height > 0 && b.x + b.width > b.x && b.y + b.height > b.y;
}
function usablePalette(p: TownPropPalette, keys: readonly (keyof TownPropPalette)[]): boolean {
  return keys.every(key => Number.isInteger(p[key]) && p[key] >= 0 && p[key] <= 0xffffff);
}
const rect = (x: number, y: number, width: number, height: number, color: number) =>
  `<rect x="${x}" y="${y}" width="${width}" height="${height}" fill="${hex(color)}" />`;

/** Opaque framed glass, using caller coordinates. The positive border must
 * leave positive inner dimensions; all details stay inside the outer frame. */
export function drawFramedPane(b: PropBounds, p: TownPropPalette, options: FramedPaneOptions = {}): string {
  const pattern = options.pattern === undefined ? 'cross' : options.pattern;
  const border = options.border === undefined ? 5 : options.border;
  if (!usableBounds(b) || !usablePalette(p, ['wood', 'woodEdge', 'glass', 'glassCore'])
    || (pattern !== 'cross' && pattern !== 'diamond') || !Number.isFinite(border) || border <= 0) return '';
  const width = b.width - border * 2, height = b.height - border * 2;
  if (!(width > 0 && height > 0)) return '';
  const x = b.x + border, y = b.y + border, cx = x + width / 2, cy = y + height / 2;
  const muntin = Math.min(border * .65, width * .12, height * .12);
  const parts = ['<g data-prop-part="framed-pane">',
    rect(b.x, b.y, b.width, b.height, p.wood),
    rect(b.x + border * .25, b.y + border * .25, b.width - border * .5, b.height - border * .5, p.woodEdge),
    rect(b.x + border * .5, b.y + border * .5, b.width - border, b.height - border, p.wood),
    rect(x, y, width, height, p.glass),
    `<path d="M${x + width * .08} ${y + height * .08} H${x + width * .42} L${x + width * .08} ${y + height * .48}Z
      M${x + width * .56} ${y + height * .56} H${x + width * .9} L${x + width * .56} ${y + height * .92}Z"
      fill="${hex(p.glassCore)}" opacity="0.45" />`,
  ];
  if (pattern === 'cross') {
    parts.push(rect(cx - muntin / 2, y, muntin, height, p.wood), rect(x, cy - muntin / 2, width, muntin, p.wood));
    parts.push(`<path d="M${cx - muntin * .15} ${y + muntin} V${y + height - muntin}
      M${x + muntin} ${cy - muntin * .15} H${x + width - muntin}" fill="none"
      stroke="${hex(p.woodEdge)}" stroke-width="${muntin * .2}" />`);
  } else {
    // Inset each vertex by the stroke radius; round joins never protrude
    // through the glass boundary, even in a very narrow pane.
    const inset = muntin / 2;
    const diamond = `M${cx} ${y + inset} L${x + width - inset} ${cy} L${cx} ${y + height - inset} L${x + inset} ${cy}Z`;
    parts.push(`<path d="${diamond}" fill="none" stroke="${hex(p.wood)}" stroke-width="${muntin}" stroke-linejoin="round" />
      <path d="${diamond}" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="${muntin * .24}" stroke-linejoin="round" />`);
  }
  parts.push('</g>');
  return parts.join('');
}

/** Alternating roof/primary cloth. Valance occupies the bottom portion of
 * bounds, with a rounded lobe per stripe and transparent gaps between lobes.
 * Zero valance makes a rectangular panel; otherwise 0 <= valance < height. */
export function drawStripedFabric(b: PropBounds, p: TownPropPalette, options: StripedFabricOptions = {}): string {
  const stripes = options.stripes === undefined ? 6 : options.stripes;
  const valance = options.valance === undefined ? 8 : options.valance;
  if (!usableBounds(b) || !usablePalette(p, ['roof', 'primary', 'shade', 'woodEdge'])
    || !Number.isSafeInteger(stripes) || stripes < 1 || stripes > MAX_PROP_DIVISIONS
    || !Number.isFinite(valance) || valance < 0 || valance >= b.height) return '';
  const stripeWidth = b.width / stripes, bodyHeight = b.height - valance;
  if (!(stripeWidth > 0 && bodyHeight > 0)) return '';
  const shoulder = b.y + bodyHeight, bottom = b.y + b.height, stroke = Math.min(stripeWidth, bodyHeight) * .03;
  const parts = ['<g data-prop-part="striped-fabric">'];
  for (let index = 0; index < stripes; index++) {
    const left = b.x + stripeWidth * index, right = Math.min(b.x + b.width, b.x + stripeWidth * (index + 1));
    const center = left + (right - left) / 2;
    const hem = valance > 0
      ? `Q${right} ${bottom} ${center} ${bottom} Q${left} ${bottom} ${left} ${shoulder}`
      : `H${left}`;
    parts.push(`<path data-fabric-stripe="${index}" d="M${left} ${b.y} H${right} V${shoulder} ${hem}Z"
      fill="${hex(index % 2 ? p.primary : p.roof)}" />`);
    parts.push(`<path d="M${left + stripeWidth * .14} ${shoulder - stroke * 2} H${right - stripeWidth * .14}"
      fill="none" stroke="${hex(p.woodEdge)}" stroke-width="${stroke}" opacity="0.6" />`);
    if (index > 0) parts.push(`<path d="M${left} ${b.y + stroke} V${shoulder - stroke}"
      fill="none" stroke="${hex(p.shade)}" stroke-width="${stroke}" />`);
  }
  parts.push('</g>');
  return parts.join('');
}
